import { useEffect, useState } from "react";

import { SCROLLAREA_CONTAINER_CLASS } from "@similarweb/ui-components";

import salesApiService from "app/pages/sales-intelligence/services/salesApiService";
import type { Integration } from "app/pages/sales-intelligence/sub-modules/management/types";

import type { Option } from "./options";
import { accountsToOptions } from "./options";

type UseSearchAccountOptionsProps = {
    isOpen: boolean;
    integrationName: Integration;
    searchTerm: string;
    containerClassName: string;
};

const ACCOUNTS_PER_PAGE = 10;

function observeIntersections(observerRoot: Element, sentinelElement: Element, onIntersection: () => void) {
    const observer = new IntersectionObserver(([entry]) => entry.isIntersecting && onIntersection(), {
        root: observerRoot,
        threshold: 1,
    });

    observer.observe(sentinelElement);

    return () => observer.disconnect();
}

type HookState = { options: Option[]; isLoading: boolean; page: number; isLastPage: boolean };

const initialState: HookState = { options: [], isLoading: true, page: 0, isLastPage: false };

export const useSearchAccountsOptions = (props: UseSearchAccountOptionsProps) => {
    const { isOpen, integrationName, searchTerm, containerClassName } = props;
    const [{ options, isLoading, page, isLastPage }, setState] = useState(initialState);

    const observeRootSelector = `.${containerClassName} .${SCROLLAREA_CONTAINER_CLASS}`;

    async function fetchAccounts(page: number, abortSignal: AbortSignal) {
        setState((state) => ({ ...state, isLoading: true }));

        try {
            const accounts = await salesApiService.contactsSearch.searchAccounts(
                searchTerm,
                integrationName,
                ACCOUNTS_PER_PAGE,
                page,
                abortSignal,
            );
            const optionsBatch = accountsToOptions(accounts);
            setState((state) => ({
                ...state,
                isLastPage: accounts.length < ACCOUNTS_PER_PAGE,
                options: page === 0 ? optionsBatch : options.concat(optionsBatch),
                isLoading: false,
                page,
            }));
        } catch (error) {
            if ((error as { name?: string })?.name === "AbortError") {return;}

            console.error(error);

            setState((state) => ({ ...state, isLoading: false }));
        }
    }

    useEffect(() => {
        if (!isOpen) {return;}

        const abortController = new AbortController();

        fetchAccounts(0, abortController.signal);

        return () => abortController.abort();
    }, [searchTerm, isOpen]);

    useEffect(() => {
        if (!isOpen || options.length === 0 || isLastPage) {return;}

        const observerRoot = document.querySelector(observeRootSelector);

        const sentinelItemId = options[options.length - 3].value;
        const sentinelElement = document.getElementById(sentinelItemId);

        if (!observerRoot || !sentinelElement) {return;}

        const abortController = new AbortController();
        const onIntersection = () => fetchAccounts(page + 1, abortController.signal);

        const finishObserving = observeIntersections(observerRoot, sentinelElement, onIntersection);

        return () => {
            finishObserving();
            abortController.abort();
        };
    }, [isOpen, options, isLastPage, page]);

    return { searchedOptions: options, isLoading };
};
